'use client';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useEffect, useRef, useState } from 'react';
export function useRegisterSubmit({ register, isDisabled, submit, }) {
    const submitRef = useRef(submit);
    submitRef.current = isDisabled ? undefined : submit;
    useEffect(() => register?.(isDisabled ? undefined : () => submitRef.current?.()), [register, isDisabled]);
}
/**
 * Maps one upload lifecycle into submission readiness.
 *
 * A stored URL is ready while parsing continues because legacy Ask submits that URL immediately.
 *
 * @returns A failed upload stays failed; otherwise success or a stored URL is ready, and the rest remain pending.
 */
export function resolveSubmitUpload({ hasStoredUrl, id, status, }) {
    switch (status) {
        case 'failed':
            return { id, status: 'failed' };
        case 'success':
            return { id, status: 'ready' };
        case 'parsing':
        case 'uploading':
        case 'validating':
        case undefined:
            return { id, status: hasStoredUrl ? 'ready' : 'pending' };
        default:
            return assertUnreachableValue(status);
    }
}
function hasFailedUpload(uploads) {
    return uploads.some((upload) => upload.status === 'failed');
}
function hasPendingUpload(uploads) {
    return uploads.some((upload) => upload.status === 'pending');
}
const composerUploadQueueEvent = EVENT_PAYLOAD;
function trackQueuedSubmitOutcome(track, surface, outcome, reason) {
    track(composerUploadQueueEvent({
        name: 'composer upload queue',
        data: { outcome, reason, surface },
    }));
}
/** Combines surface policy with the shared upload queue's disabled states. */
export function resolveUploadAwareSubmitDisabled({ isDisabled, isSubmitBlocked, isSubmitQueued, }) {
    return isDisabled || isSubmitBlocked || isSubmitQueued;
}
/**
 * Preserves one submit gesture while selected uploads settle.
 *
 * Removing or failing a selected upload cancels the gesture. A later selection or draft edit also
 * cancels so new content cannot join without confirmation. Attachment metadata resolves when all
 * uploads are ready while the submit callback remains bound to the original gesture.
 */
export function useSubmitAfterUploads({ analyticsSurface, cancellationVersion = 0, hasUploadFailure = false, isUploadPending = false, isUploadRegistrationPending, isSubmissionBlocked = false, onSubmit, onSubmitRequestQueued, resolveSubmission, submissionResourceIds = EMPTY_ARRAY, submissionRevision, uploadSelectionVersion = 0, uploads, }) {
    const { track } = useInstrumentation();
    const [isSubmitQueued, setIsSubmitQueued] = useState(false);
    const queuedSubmissionRef = useRef(null);
    const resolveSubmissionRef = useRef(resolveSubmission);
    resolveSubmissionRef.current = resolveSubmission;
    const uploadRegistrationPending = isUploadRegistrationPending ?? (isUploadPending && !hasPendingUpload(uploads));
    const submissionIsBlocked = useCallback((submission) => typeof isSubmissionBlocked === 'function'
        ? isSubmissionBlocked(submission)
        : isSubmissionBlocked, [isSubmissionBlocked]);
    const dispatchSubmission = useCallback(({ fromQueue, submission, submit, }) => {
        const latestResolver = resolveSubmissionRef.current;
        const resolvedSubmission = latestResolver
            ? latestResolver(submission, { fromQueue })
            : submission;
        return submit(resolvedSubmission);
    }, []);
    const cancelQueuedSubmit = useCallback((reason, isUnmounting = false) => {
        const queuedSubmission = queuedSubmissionRef.current;
        if (queuedSubmission === null)
            return;
        queuedSubmission.onCancel(reason);
        queuedSubmissionRef.current = null;
        if (!isUnmounting)
            setIsSubmitQueued(false);
    }, []);
    useEffect(() => () => cancelQueuedSubmit('unmount', true), [cancelQueuedSubmit]);
    const requestSubmit = useCallback((submission) => {
        if (queuedSubmissionRef.current !== null)
            return false;
        if (submissionIsBlocked(submission) || hasFailedUpload(uploads))
            return false;
        const isWaitingForUpload = isUploadPending || hasPendingUpload(uploads);
        if (isWaitingForUpload && hasUploadFailure)
            return false;
        if (!isWaitingForUpload) {
            return dispatchSubmission({ fromQueue: false, submission, submit: onSubmit });
        }
        queuedSubmissionRef.current = {
            awaitedIdWasRegistered: false,
            awaitingIds: uploadRegistrationPending,
            cancellationVersion,
            ids: uploads.map((upload) => upload.id),
            onCancel: (reason) => trackQueuedSubmitOutcome(track, analyticsSurface, 'canceled', reason),
            payload: submission,
            resources: [...submissionResourceIds],
            selection: uploadSelectionVersion,
            submissionRevision,
            submit: onSubmit,
        };
        trackQueuedSubmitOutcome(track, analyticsSurface, 'queued');
        setIsSubmitQueued(true);
        onSubmitRequestQueued?.();
        return false;
    }, [
        analyticsSurface,
        cancellationVersion,
        dispatchSubmission,
        hasUploadFailure,
        isUploadPending,
        uploadRegistrationPending,
        onSubmit,
        onSubmitRequestQueued,
        submissionResourceIds,
        submissionRevision,
        submissionIsBlocked,
        track,
        uploadSelectionVersion,
        uploads,
    ]);
    useEffect(() => {
        const queuedSubmission = queuedSubmissionRef.current;
        if (queuedSubmission === null)
            return;
        if (queuedSubmission.selection !== uploadSelectionVersion) {
            cancelQueuedSubmit('added');
            return;
        }
        if (!Object.is(queuedSubmission.submissionRevision, submissionRevision)) {
            cancelQueuedSubmit('edited');
            return;
        }
        if (queuedSubmission.cancellationVersion !== cancellationVersion) {
            cancelQueuedSubmit('dismissed');
            return;
        }
        const currentUploadIds = uploads.map((upload) => upload.id);
        if (queuedSubmission.awaitingIds) {
            const registeredUploadIds = currentUploadIds.filter((id) => !queuedSubmission.ids.includes(id));
            if (registeredUploadIds.length > 0) {
                queuedSubmission.ids.push(...registeredUploadIds);
                queuedSubmission.awaitedIdWasRegistered = true;
            }
            if (!uploadRegistrationPending) {
                queuedSubmission.awaitingIds = false;
                if (!queuedSubmission.awaitedIdWasRegistered) {
                    cancelQueuedSubmit('missing');
                    return;
                }
            }
        }
        const unexpectedUploadWasAdded = currentUploadIds.some((id) => !queuedSubmission.ids.includes(id));
        const unexpectedResourceWasAdded = submissionResourceIds.some((id) => !queuedSubmission.resources.includes(id));
        if (unexpectedUploadWasAdded || unexpectedResourceWasAdded) {
            cancelQueuedSubmit('added');
            return;
        }
        const requestedUploadWasRemoved = queuedSubmission.ids.some((id) => !currentUploadIds.includes(id));
        if (requestedUploadWasRemoved) {
            cancelQueuedSubmit('removed');
            return;
        }
        if (hasFailedUpload(uploads) || hasUploadFailure) {
            cancelQueuedSubmit('failed');
            return;
        }
        if (submissionIsBlocked(queuedSubmission.payload))
            return;
        const uploadsArePending = isUploadPending || hasPendingUpload(uploads);
        if (uploadsArePending)
            return;
        // Clear synchronously before invoking consumer code so a rerender or duplicate effect cannot
        // dispatch the same user gesture twice.
        queuedSubmissionRef.current = null;
        setIsSubmitQueued(false);
        dispatchSubmission({
            fromQueue: true,
            submission: queuedSubmission.payload,
            submit: queuedSubmission.submit,
        });
    }, [
        cancelQueuedSubmit,
        cancellationVersion,
        dispatchSubmission,
        hasUploadFailure,
        isUploadPending,
        uploadRegistrationPending,
        submissionResourceIds,
        submissionRevision,
        submissionIsBlocked,
        uploadSelectionVersion,
        uploads,
    ]);
    return {
        isSubmitBlocked: hasFailedUpload(uploads),
        isSubmitQueued,
        requestSubmit,
    };
}
//# sourceMappingURL=useSubmitAfterUploads.js.map